import {
  AssignTeamPayload,
  PartialPickPayload,
  PickRecordPayload,
} from 'common/api/dto/harvestDataApi.types';
import {
  EditScheduledPickPayload,
  ScheduledPickPayload,
} from 'common/api/dto/pickSchedulingApi.types';
import {
  useAssignTeamMutation,
  useSavePartialPickRecordsMutation,
  useSavePickRecordMutation,
} from 'common/api/endpoints/harvestData';
import {
  useEditScheduledPickMutation,
  useSchedulePickMutation,
} from 'common/api/endpoints/schedule';
import { handleError } from 'common/api/utils/handleError';
import { isApiError } from 'common/api/utils/isApiError';
import * as notifier from 'common/services/notification';
import { useConfirmationModal } from 'features/confirmation-modal';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

type PickScheduleActions = {
  openCancelSchedulingModal: () => void;
  savePickToSchedule: (
    pick: ScheduledPickPayload,
    force?: boolean,
  ) => Promise<'OK' | 'FAILED' | 'PENDING'>;
  assignHarvestTeam: (team: AssignTeamPayload) => Promise<boolean>;
  savePartialPickData: (body: PartialPickPayload[]) => Promise<boolean>;
  savePickData: (data: PickRecordPayload) => Promise<boolean>;
  tryEditScheduledPick: (payload: EditScheduledPickPayload) => Promise<boolean>;
};

const usePickScheduleActions = (): PickScheduleActions => {
  const history = useHistory();
  const { openModal } = useConfirmationModal();
  const [schedulePick] = useSchedulePickMutation();
  const [editScheduledPick] = useEditScheduledPickMutation();
  const [assignTeamForPick] = useAssignTeamMutation();
  const [savePartialPickRecords] = useSavePartialPickRecordsMutation();
  const [savePickRecord] = useSavePickRecordMutation();

  const openCancelSchedulingModal = () => {
    openModal({
      title: 'If you exit, your changes will not be saved.',
      confirmButtonLabel: 'EXIT',
      declineButtonLabel: 'STAY',
      onConfirm: () => history.goBack(),
    });
  };

  const savePickToSchedule = useCallback(
    async (pick: ScheduledPickPayload, force?: boolean) => {
      try {
        await schedulePick({ ...pick, allowMultiplePicks: force }).unwrap();
        notifier.showSuccessMessage('Pick scheduled.');
        return 'OK';
      } catch (error) {
        const isExpectedException = isApiError(error);
        if (isExpectedException && error.status === 472) {
          return 'PENDING';
        }

        handleError(error, {
          462: 'Missing market or size. Specify both or neither of them.',
          471: 'This block has already been scheduled for this packing house.',
          default: 'Unable to schedule pick, please try again later.',
        });

        return 'FAILED';
      }
    },
    [schedulePick],
  );

  const assignHarvestTeam = useCallback(
    async (team: AssignTeamPayload) => {
      try {
        await assignTeamForPick(team).unwrap();
        notifier.showSuccessMessage('Team assigned.');
        return true;
      } catch (error) {
        handleError(error, 'Unable to assign team, please try again later.');
        return false;
      }
    },
    [assignTeamForPick],
  );

  const savePartialPickData = useCallback(
    async (body: PartialPickPayload[]) => {
      try {
        await savePartialPickRecords(body).unwrap();
        notifier.showSuccessMessage('Pick data partially saved.');
        return true;
      } catch (error) {
        handleError(error, 'Unable to save partial pick data.');

        return false;
      }
    },
    [savePartialPickRecords],
  );

  const savePickData = useCallback(
    async (data: PickRecordPayload) => {
      try {
        await savePickRecord(data).unwrap();
        notifier.showSuccessMessage('Pick data saved.');
        return true;
      } catch (error) {
        handleError(error, {
          461:
            'Bins left cannot be changed. A more recent estimate ' +
            'has already been entered.',
          default: 'Unable to save pick data, please try again later.',
        });

        return false;
      }
    },
    [savePickRecord],
  );

  const tryEditScheduledPick = async (payload: EditScheduledPickPayload) => {
    try {
      await editScheduledPick(payload).unwrap();
      return true;
    } catch (error) {
      handleError(
        error,
        'Unable to edit scheduled pick, please try again later.',
      );
      return false;
    }
  };

  return {
    openCancelSchedulingModal,
    savePickToSchedule,
    assignHarvestTeam,
    savePartialPickData,
    savePickData,
    tryEditScheduledPick,
  };
};

export default usePickScheduleActions;
